import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Briefcase, Bookmark } from "lucide-react";

export function AssignJobsButton({ candidateId, onAssigned }) {
    const [open, setOpen] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [picked, setPicked] = useState(new Set());
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            api.get("/jobs", { params: { status: "active" } }).then((r) => setJobs(r.data || [])).catch(() => {});
        }
    }, [open]);

    const submit = async () => {
        if (picked.size === 0) return toast.error("Pick at least one job");
        setBusy(true);
        try {
            const { data } = await api.post(`/candidates/${candidateId}/assign-jobs`, { job_ids: [...picked] });
            const ok = data.results.filter((r) => r.ok && !r.skipped).length;
            const skipped = data.results.filter((r) => r.skipped).length;
            toast.success(`Assigned to ${ok} job${ok === 1 ? "" : "s"}${skipped ? `, ${skipped} already assigned` : ""}`);
            setOpen(false);
            setPicked(new Set());
            onAssigned && onAssigned();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="rounded-none h-10" data-testid="assign-jobs-btn"><Briefcase className="w-4 h-4 mr-2" /> Add to job</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-xl">
                <DialogHeader>
                    <DialogTitle className="font-display font-bold text-2xl">Assign to jobs</DialogTitle>
                </DialogHeader>
                <div className="max-h-[50vh] overflow-y-auto border border-border divide-y divide-border">
                    {jobs.length === 0 ? (
                        <div className="p-6 text-sm text-muted-foreground text-center">No active jobs</div>
                    ) : jobs.map((j) => (
                        <label key={j.id} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-secondary">
                            <Checkbox
                                checked={picked.has(j.id)}
                                onCheckedChange={(v) => {
                                    const s = new Set(picked);
                                    if (v) s.add(j.id); else s.delete(j.id);
                                    setPicked(s);
                                }}
                                className="rounded-none mt-1"
                                data-testid={`assign-job-${j.id}`}
                            />
                            <div className="flex-1">
                                <div className="font-medium text-sm">{j.title}</div>
                                <div className="text-xs text-muted-foreground font-mono">{j.location || "Remote"} · {j.employment_type} · {j.applicant_count} candidates</div>
                            </div>
                        </label>
                    ))}
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={busy || picked.size === 0} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="assign-confirm">{busy ? "Assigning..." : `Assign (${picked.size})`}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function AddToListButton({ candidateId, onAdded }) {
    const [open, setOpen] = useState(false);
    const [lists, setLists] = useState([]);
    const [picked, setPicked] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const [newName, setNewName] = useState("");

    useEffect(() => {
        if (open) {
            api.get("/hiring-lists").then((r) => setLists(r.data || [])).catch(() => {});
        }
    }, [open]);

    const submit = async () => {
        setBusy(true);
        try {
            for (const lid of picked) {
                await api.post(`/hiring-lists/${lid}/members`, { candidate_ids: [candidateId] });
            }
            toast.success(`Added to ${picked.size} list${picked.size === 1 ? "" : "s"}`);
            setOpen(false);
            setPicked(new Set());
            onAdded && onAdded();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    const createList = async () => {
        if (!newName.trim()) return;
        try {
            const { data } = await api.post("/hiring-lists", { name: newName.trim() });
            await api.post(`/hiring-lists/${data.id}/members`, { candidate_ids: [candidateId] });
            toast.success(`Created “${data.name}” and added candidate`);
            setNewName("");
            const { data: refreshed } = await api.get("/hiring-lists");
            setLists(refreshed || []);
            onAdded && onAdded();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="rounded-none h-10" data-testid="add-to-list-btn"><Bookmark className="w-4 h-4 mr-2" /> Add to list</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display font-bold text-2xl">Add to hiring list</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    {lists.length === 0 && <div className="text-sm text-muted-foreground">No lists yet — create one below.</div>}
                    {lists.length > 0 && (
                        <div className="border border-border divide-y divide-border max-h-64 overflow-y-auto">
                            {lists.map((l) => (
                                <label key={l.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary">
                                    <Checkbox
                                        checked={picked.has(l.id)}
                                        onCheckedChange={(v) => {
                                            const s = new Set(picked);
                                            if (v) s.add(l.id); else s.delete(l.id);
                                            setPicked(s);
                                        }}
                                        className="rounded-none"
                                        data-testid={`pick-list-${l.id}`}
                                    />
                                    <div className="flex-1 text-sm">
                                        <div className="font-medium">{l.name}</div>
                                        <div className="text-xs text-muted-foreground">{l.member_count} candidates</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                    <div className="flex gap-2 pt-2 border-t border-border">
                        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New list name" className="rounded-none h-9" data-testid="new-list-name" />
                        <Button type="button" variant="outline" className="rounded-none h-9" onClick={createList} data-testid="create-list-inline">Create + add</Button>
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={busy || picked.size === 0} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="add-to-list-confirm">{busy ? "Adding..." : `Add to ${picked.size} list${picked.size === 1 ? "" : "s"}`}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
